import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Plus, Trash2, RotateCcw, Image as ImageIcon } from "lucide-react";
import { api, formatApiError } from "../../api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../ui/accordion";

function ImagePicker({ value, onChange, label = "Image" }) {
  const inp = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image téléchargée");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider text-white/70">{label}</Label>
      <div className="flex items-start gap-3">
        <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md border border-white/10 bg-black/40">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-white/30"><ImageIcon size={20} /></div>}
        </div>
        <div className="flex-1 space-y-2">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL de l'image" className="border-white/10 bg-black/50 text-xs" />
          <input ref={inp} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
          <button type="button" onClick={() => inp.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/5 disabled:opacity-50">
            <Upload size={12} />{busy ? "Envoi…" : "Uploader une image"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemList({ items = [], onChange, fields = ["title", "desc"], labels = { title: "Titre", desc: "Description" } }) {
  const setItem = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f, ""]))]);
  const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="grid gap-3 rounded-md border border-white/10 bg-black/40 p-3 md:grid-cols-12">
          {fields.map((f) => (
            <div key={f} className={`space-y-1 ${f === "desc" || f === "description" ? "md:col-span-8" : "md:col-span-3"}`}>
              <Label className="text-[10px] uppercase tracking-wider text-white/60">{labels[f] || f}</Label>
              {f === "desc" || f === "description" ? (
                <Textarea rows={2} value={it[f] || ""} onChange={(e) => setItem(i, f, e.target.value)} className="border-white/10 bg-black/50 text-sm" />
              ) : (
                <Input value={it[f] || ""} onChange={(e) => setItem(i, f, e.target.value)} className="border-white/10 bg-black/50 text-sm" />
              )}
            </div>
          ))}
          <div className="flex items-end md:col-span-1">
            <button onClick={() => rm(i)} className="rounded-md border border-red-500/30 px-2 py-2 text-red-400 hover:bg-red-500/10"><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/5"><Plus size={12} />Ajouter</button>
    </div>
  );
}

function GalleryList({ items = [], onChange }) {
  const setItem = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => onChange([...items, { img: "", title: "", tag: "" }]);
  const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="grid gap-3 rounded-md border border-white/10 bg-black/40 p-3 md:grid-cols-12">
          <div className="md:col-span-6">
            <ImagePicker value={it.img} onChange={(v) => setItem(i, "img", v)} label={`Image ${i + 1}`} />
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label className="text-[10px] uppercase tracking-wider text-white/60">Titre</Label>
            <Input value={it.title || ""} onChange={(e) => setItem(i, "title", e.target.value)} className="border-white/10 bg-black/50 text-sm" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-white/60">Tag</Label>
            <Input value={it.tag || ""} onChange={(e) => setItem(i, "tag", e.target.value)} className="border-white/10 bg-black/50 text-sm" />
          </div>
          <div className="flex items-end md:col-span-1">
            <button onClick={() => rm(i)} className="rounded-md border border-red-500/30 px-2 py-2 text-red-400 hover:bg-red-500/10"><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/5"><Plus size={12} />Ajouter une image</button>
    </div>
  );
}

export default function ContentEditor() {
  const [content, setContent] = useState(null);
  const [initial, setInitial] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/content");
    setContent(data); setInitial(JSON.stringify(data));
  };
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => content && initial !== JSON.stringify(content), [content, initial]);

  if (!content) return <div className="text-white/50">Chargement…</div>;

  const setPath = (path, value) => {
    setContent((c) => {
      const clone = JSON.parse(JSON.stringify(c));
      const keys = path.split("."); let obj = clone;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]] ||= {};
      obj[keys[keys.length - 1]] = value;
      return clone;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", { data: content });
      toast.success("Contenu enregistré. Rechargez le site public pour voir les changements.");
      setInitial(JSON.stringify(content));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reset = () => { if (window.confirm("Annuler toutes les modifications ?")) load(); };

  return (
    <div className="space-y-6" data-testid="content-editor">
      <div className="sticky top-16 z-20 -mx-5 flex items-center justify-between border-b border-white/10 bg-[#050505]/85 px-5 py-3 backdrop-blur sm:-mx-8 sm:px-8">
        <div>
          <p className="text-xs uppercase tracking-wider text-white/40">Éditeur de contenu</p>
          <p className="text-sm text-white/70">Modifiez tous les textes et photos du site public.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reset} disabled={!dirty} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-white/80 hover:bg-white/5 disabled:opacity-40"><RotateCcw size={12} />Annuler</button>
          <button onClick={save} disabled={busy || !dirty} className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-5 py-2 text-xs font-semibold text-white hover:scale-[1.02] transition-transform disabled:opacity-40" data-testid="content-save-btn">
            <Save size={12} />{busy ? "Enregistrement…" : dirty ? "Enregistrer" : "Enregistré"}
          </button>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["hero"]} className="space-y-3">
        {/* HERO */}
        <AccordionItem value="hero" className="rounded-lg border border-white/10 bg-[#0f0f0f]">
          <AccordionTrigger className="px-5 py-4 text-left font-head text-base uppercase tracking-tight text-white hover:no-underline">Hero (accueil)</AccordionTrigger>
          <AccordionContent className="space-y-4 px-5 pb-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/70">Sur-titre</Label>
              <Input value={content.hero.eyebrow || ""} onChange={(e) => setPath("hero.eyebrow", e.target.value)} className="border-white/10 bg-black/50" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/70">Titre (3 lignes)</Label>
              {(content.hero.lines || ["", "", ""]).map((l, i) => (
                <Input key={i} value={l} onChange={(e) => {
                  const arr = [...(content.hero.lines || ["", "", ""])];
                  arr[i] = e.target.value; setPath("hero.lines", arr);
                }} placeholder={`Ligne ${i + 1}`} className="border-white/10 bg-black/50" />
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/70">Sous-titre</Label>
              <Textarea rows={3} value={content.hero.subtitle || ""} onChange={(e) => setPath("hero.subtitle", e.target.value)} className="border-white/10 bg-black/50" />
            </div>
            <ImagePicker value={content.hero.image} onChange={(v) => setPath("hero.image", v)} label="Image d'arrière-plan" />
          </AccordionContent>
        </AccordionItem>

        {/* MAINTENANCE */}
        <AccordionItem value="maintenance" className="rounded-lg border border-white/10 bg-[#0f0f0f]">
          <AccordionTrigger className="px-5 py-4 text-left font-head text-base uppercase tracking-tight text-white hover:no-underline">Services — Maintenance</AccordionTrigger>
          <AccordionContent className="space-y-4 px-5 pb-5">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Tag</Label><Input value={content.maintenance.tag || ""} onChange={(e) => setPath("maintenance.tag", e.target.value)} className="border-white/10 bg-black/50" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Titre</Label><Input value={content.maintenance.label || ""} onChange={(e) => setPath("maintenance.label", e.target.value)} className="border-white/10 bg-black/50" /></div>
            </div>
            <ImagePicker value={content.maintenance.image} onChange={(v) => setPath("maintenance.image", v)} label="Image de la section" />
            <div>
              <Label className="text-xs uppercase text-white/70">Items</Label>
              <div className="mt-2"><ItemList items={content.maintenance.items || []} onChange={(v) => setPath("maintenance.items", v)} /></div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* EVENEMENTIEL */}
        <AccordionItem value="evenementiel" className="rounded-lg border border-white/10 bg-[#0f0f0f]">
          <AccordionTrigger className="px-5 py-4 text-left font-head text-base uppercase tracking-tight text-white hover:no-underline">Services — Événementiel</AccordionTrigger>
          <AccordionContent className="space-y-4 px-5 pb-5">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Tag</Label><Input value={content.evenementiel.tag || ""} onChange={(e) => setPath("evenementiel.tag", e.target.value)} className="border-white/10 bg-black/50" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Titre</Label><Input value={content.evenementiel.label || ""} onChange={(e) => setPath("evenementiel.label", e.target.value)} className="border-white/10 bg-black/50" /></div>
            </div>
            <ImagePicker value={content.evenementiel.image} onChange={(v) => setPath("evenementiel.image", v)} label="Image de la section" />
            <div>
              <Label className="text-xs uppercase text-white/70">Items</Label>
              <div className="mt-2"><ItemList items={content.evenementiel.items || []} onChange={(v) => setPath("evenementiel.items", v)} /></div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* MANIFESTO */}
        <AccordionItem value="manifesto" className="rounded-lg border border-white/10 bg-[#0f0f0f]">
          <AccordionTrigger className="px-5 py-4 text-left font-head text-base uppercase tracking-tight text-white hover:no-underline">Manifeste</AccordionTrigger>
          <AccordionContent className="space-y-4 px-5 pb-5">
            <ItemList items={content.manifesto || []} onChange={(v) => setPath("manifesto", v)} fields={["num", "title", "desc"]} labels={{ num: "N°", title: "Titre", desc: "Description" }} />
          </AccordionContent>
        </AccordionItem>

        {/* GALLERY */}
        <AccordionItem value="gallery" className="rounded-lg border border-white/10 bg-[#0f0f0f]">
          <AccordionTrigger className="px-5 py-4 text-left font-head text-base uppercase tracking-tight text-white hover:no-underline">Galerie / Réalisations</AccordionTrigger>
          <AccordionContent className="space-y-4 px-5 pb-5">
            <GalleryList items={content.gallery || []} onChange={(v) => setPath("gallery", v)} />
          </AccordionContent>
        </AccordionItem>

        {/* CONTACT */}
        <AccordionItem value="contact" className="rounded-lg border border-white/10 bg-[#0f0f0f]">
          <AccordionTrigger className="px-5 py-4 text-left font-head text-base uppercase tracking-tight text-white hover:no-underline">Contact & Réseaux</AccordionTrigger>
          <AccordionContent className="grid gap-3 px-5 pb-5 md:grid-cols-2">
            <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Téléphone affiché</Label><Input value={content.contact.phone || ""} onChange={(e) => setPath("contact.phone", e.target.value)} className="border-white/10 bg-black/50" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Téléphone (chiffres uniquement)</Label><Input value={content.contact.phone_raw || ""} onChange={(e) => setPath("contact.phone_raw", e.target.value)} className="border-white/10 bg-black/50" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">WhatsApp (format international)</Label><Input value={content.contact.whatsapp || ""} onChange={(e) => setPath("contact.whatsapp", e.target.value)} className="border-white/10 bg-black/50" placeholder="33612345678" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Email</Label><Input value={content.contact.email || ""} onChange={(e) => setPath("contact.email", e.target.value)} className="border-white/10 bg-black/50" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Instagram (handle)</Label><Input value={content.contact.instagram || ""} onChange={(e) => setPath("contact.instagram", e.target.value)} className="border-white/10 bg-black/50" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Instagram URL</Label><Input value={content.contact.instagram_url || ""} onChange={(e) => setPath("contact.instagram_url", e.target.value)} className="border-white/10 bg-black/50" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Facebook (nom)</Label><Input value={content.contact.facebook || ""} onChange={(e) => setPath("contact.facebook", e.target.value)} className="border-white/10 bg-black/50" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-white/70">Facebook URL</Label><Input value={content.contact.facebook_url || ""} onChange={(e) => setPath("contact.facebook_url", e.target.value)} className="border-white/10 bg-black/50" /></div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
